import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "../Components/SideBar";
import SubscriptionBanner from "../modules/saas/components/SubscriptionBanner";

const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

const MainLayout = ({ totalnewOrder }) => {
    const [collapsed, setCollapsed] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [viewport, setViewport] = useState(() => ({
        isMobile: window.innerWidth <= MOBILE_MAX,
        isTablet: window.innerWidth > MOBILE_MAX && window.innerWidth <= TABLET_MAX,
        isDesktop: window.innerWidth > TABLET_MAX,
    }));
    const location = useLocation();

    const { isMobile, isTablet } = viewport;
    const isOverlay = isMobile || isTablet;

    const isBarePage =
        ["/login", "/reset", "/home/login"].includes(location.pathname) ||
        location.pathname === "/" ||
        location.pathname === "/pricing" ||
        location.pathname === "/compare" ||
        location.pathname === "/signup" ||
        location.pathname === "/verify-email" ||
        location.pathname.startsWith("/payment/") ||
        location.pathname.startsWith("/portal") ||
        location.pathname.startsWith("/pay/invoice/") ||
        location.pathname.startsWith("/crm-partner") ||
        location.pathname.startsWith("/crm-sign/");

    useEffect(() => {
        document.body.classList.toggle("sidebar-mobile-open", mobileOpen && isOverlay);
        return () => document.body.classList.remove("sidebar-mobile-open");
    }, [mobileOpen, isOverlay]);

    useEffect(() => {
        const syncViewport = () => {
            const w = window.innerWidth;
            setViewport({
                isMobile: w <= MOBILE_MAX,
                isTablet: w > MOBILE_MAX && w <= TABLET_MAX,
                isDesktop: w > TABLET_MAX,
            });
        };

        syncViewport();
        window.addEventListener("resize", syncViewport);
        return () => window.removeEventListener("resize", syncViewport);
    }, []);

    useEffect(() => {
        if (isOverlay) {
            setCollapsed(false);
            setMobileOpen(false);
        } else {
            setCollapsed(true);
            setMobileOpen(false);
        }
    }, [isOverlay]);

    /* Portals (SweetAlert2, etc.) mount on document.body — theme must live on <html>. */
    useEffect(() => {
        const root = document.documentElement;
        if (isBarePage) {
            root.removeAttribute("data-admin-theme");
        } else {
            root.setAttribute("data-admin-theme", "dark");
        }
        return () => root.removeAttribute("data-admin-theme");
    }, [isBarePage]);

    const closeMobileMenu = () => setMobileOpen(false);

    const sidebarWidth = isOverlay ? 0 : collapsed ? 72 : 280;

    if (isBarePage) {
        return (
            <div className="min-h-screen">
                <Outlet />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-admin-theme="dark">
            <Sidebar
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
                isMobile={isMobile}
                isTablet={isTablet}
                onCloseMobile={closeMobileMenu}
                totalnewOrder={totalnewOrder}
            />

            <main
                className="transition-all duration-300 ease-in-out"
                style={{
                    marginLeft: isOverlay ? 0 : `${sidebarWidth}px`,
                    paddingTop: isOverlay ? "calc(3.25rem + env(safe-area-inset-top, 0px))" : 0,
                    pointerEvents: mobileOpen && isOverlay ? "none" : "auto",
                }}
                onClick={isOverlay ? closeMobileMenu : undefined}
            >
                <div className="p-2 sm:p-3 md:p-4">
                    <SubscriptionBanner />
                    <Outlet key={location.key} />
                    <span className="admin-page-bottom-span" aria-hidden="true" />
                </div>
            </main>

            {mobileOpen && isOverlay && (
                <div
                    className="fixed inset-0 bg-black/55 z-[125] backdrop-blur-[2px]"
                    onClick={closeMobileMenu}
                    aria-hidden
                />
            )}
        </div>
    );
};

export default MainLayout;
